import { RRule, Weekday } from "rrule";
import { DateTime } from "luxon";
import {
  getEventTimezone,
  toCalendarWallDate,
  toEventWallDateTime,
} from "../../util/eventDateTime";
import { getSlotSummaryForOccurrence } from "../../util/slotOccurrences";

const DEFAULT_RANGE_BUFFER_DAYS = 7;
const MAX_OCCURRENCES_PER_EVENT = 300;

const toDate = (value, timezone = "local") => {
  if (!value) return null;
  return toCalendarWallDate(value, timezone);
};

const toUTCWallDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return DateTime.utc(
    date.getFullYear(),
    date.getMonth() + 1,
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds()
  ).toJSDate();
};

const toRRuleWallDate = (value, timezone = "local") => {
  const dateTime = toEventWallDateTime(value, timezone);
  if (!dateTime) return null;
  return DateTime.utc(
    dateTime.year,
    dateTime.month,
    dateTime.day,
    dateTime.hour,
    dateTime.minute,
    dateTime.second,
    dateTime.millisecond
  ).toJSDate();
};

const fromRRuleWallDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    date.getUTCMilliseconds()
  );
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const addDuration = (start, durationMs) => new Date(start.getTime() + durationMs);

const getNormalizedStatus = (status) =>
  String(status || "active")
    .toLowerCase()
    .replaceAll(" ", "_");

const getStatusClass = (status) => {
  const normalized = getNormalizedStatus(status);

  if (normalized === "rejected") return "rejected";
  if (normalized === "awaiting_review" || normalized === "pending") {
    return "awaiting-review";
  }

  return "active";
};

const getStatusLabel = (status) => getNormalizedStatus(status).replaceAll("_", " ");

export const getCalendarStatusClass = getStatusClass;

const getEventDateRange = (event) => {
  const timezone = getEventTimezone(event);
  const activeDate = event?.nextOccurence || event?.date;
  const start = toDate(activeDate?.start, timezone);
  const end = toDate(activeDate?.end, timezone);

  return {
    start,
    end: end || (start ? addDuration(start, 60 * 60 * 1000) : null),
  };
};

const cleanRRuleOption = (value) => {
  if (value === null || value === undefined || value === "") return undefined;
  return value;
};

const normalizeWeekday = (weekday) => {
  if (typeof weekday === "number") return weekday;

  if (typeof weekday === "string") {
    const upper = weekday.toUpperCase().slice(0, 2);
    return {
      MO: RRule.MO,
      TU: RRule.TU,
      WE: RRule.WE,
      TH: RRule.TH,
      FR: RRule.FR,
      SA: RRule.SA,
      SU: RRule.SU,
    }[upper];
  }

  if (typeof weekday === "object") {
    const day = weekday.weekday ?? weekday._weekday;
    const nth = weekday.n ?? weekday._n;

    if (typeof day === "number") {
      return nth ? new Weekday(day, nth) : day;
    }
  }

  return undefined;
};

const normalizeRRuleOptions = (event, baseStart) => {
  const timezone = getEventTimezone(event);
  const rawOptions = event?.rrule?.origOptions || event?.rrule?.options || event?.rrule;
  if (!rawOptions || typeof rawOptions !== "object") return null;

  const options = Object.entries(rawOptions).reduce((acc, [key, value]) => {
    const cleanValue = cleanRRuleOption(value);
    if (cleanValue !== undefined) acc[key] = cleanValue;
    return acc;
  }, {});

  options.dtstart = toRRuleWallDate(options.dtstart, timezone) || toUTCWallDate(baseStart);

  if (options.until) {
    options.until = toRRuleWallDate(options.until, timezone);
  }

  if (Array.isArray(options.byweekday)) {
    options.byweekday = options.byweekday.map(normalizeWeekday).filter(Boolean);
  } else if (options.byweekday !== undefined) {
    options.byweekday = normalizeWeekday(options.byweekday);
  }

  if (!options.dtstart || options.freq === undefined) return null;

  return options;
};

const removeTimeFromRRuleText = (text = "") =>
  text
    .replace(/\s+at\s+\d{1,2}(:\d{2})?(\s?(am|pm))?/gi, "")
    .replace(/\s+at\s+\d{1,2}(:\d{2})?(:\d{2})?/gi, "")
    .trim();

const getRecurrenceText = ({ event, rruleOptions }) => {
  try {
    if (rruleOptions) {
      const ruleText = removeTimeFromRRuleText(
        new RRule(rruleOptions).toText()?.toLowerCase()
      );

      return ruleText ? `repeats ${ruleText}` : "repeats";
    }
  } catch (error) {
    console.warn("failed to format recurring event", event?._id, error);
  }

  if (event?.recurring) {
    const frequency = event.frequency ? ` ${event.frequency}` : "";
    const end = toDate(event.recurringEnd);
    const endText = end
      ? ` until ${end.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        })}`
      : "";

    return `repeats${frequency}${endText}`;
  }

  return "";
};

const getSlotSummary = (event, occurrenceStart) =>
  getSlotSummaryForOccurrence(event, occurrenceStart);

const buildCalendarEvent = ({
  event,
  start,
  end,
  occurrenceStart = start,
  occurrenceEnd = end,
  occurrenceIndex = 0,
  isRecurring = false,
  recurrenceText = "",
  isCondensedRejectedRecurring = false,
}) => {
  const statusClass = getStatusClass(event.status);
  const slotSummary = getSlotSummary(event, occurrenceStart);

  return {
    id: `${event._id || event.id}-${start.toISOString()}-${occurrenceIndex}`,
    title: event.name || "untitled event",
    start,
    end,
    status: event.status,
    classNames: [
      `app-calendar-event--${statusClass}`,
      isRecurring ? "app-calendar-event--recurring" : "",
      isCondensedRejectedRecurring
        ? "app-calendar-event--condensed-rejected-recurring"
        : "",
    ].filter(Boolean),
    extendedProps: {
      sourceType: "posted-event",
      parentEvent: event,
      occurrenceStart,
      occurrenceEnd,
      status: event.status,
      statusClass,
      statusLabel: getStatusLabel(event.status),
      isRecurring,
      recurrenceText,
      isCondensedRejectedRecurring,
      ...slotSummary,
      venue: event.venue,
      address: event.address,
    },
  };
};

export const expandPostedEventsForCalendar = (
  events = [],
  visibleRange = {},
  options = {}
) => {
  const rangeStart = toDate(visibleRange.start) || addDays(new Date(), -DEFAULT_RANGE_BUFFER_DAYS);
  const rangeEnd = toDate(visibleRange.end) || addDays(new Date(), 90);
  const {
    statusFilters = ["active", "awaiting-review", "rejected"],
    condenseRejectedRecurring = true,
  } = options;
  const enabledStatuses = new Set(statusFilters);

  return events.filter((event) => enabledStatuses.has(getStatusClass(event.status))).flatMap((event) => {
    const { start: baseStart, end: baseEnd } = getEventDateRange(event);
    if (!baseStart || !baseEnd) return [];

    const durationMs = Math.max(baseEnd.getTime() - baseStart.getTime(), 30 * 60 * 1000);
    const rruleOptions = normalizeRRuleOptions(event, baseStart);
    const isRecurring = Boolean(rruleOptions || event?.recurring);
    const recurrenceText = getRecurrenceText({ event, rruleOptions });
    const isRejected = getStatusClass(event.status) === "rejected";

    if (!rruleOptions || (isRejected && condenseRejectedRecurring)) {
      return [
        buildCalendarEvent({
          event,
          start: baseStart,
          end: baseEnd,
          isRecurring,
          recurrenceText,
          isCondensedRejectedRecurring:
            isRejected && isRecurring && condenseRejectedRecurring,
        }),
      ];
    }

    try {
      const rule = new RRule(rruleOptions);
      const occurrences = rule
        .between(
          toUTCWallDate(addDays(rangeStart, -DEFAULT_RANGE_BUFFER_DAYS)),
          toUTCWallDate(addDays(rangeEnd, DEFAULT_RANGE_BUFFER_DAYS)),
          true
        )
        .slice(0, MAX_OCCURRENCES_PER_EVENT);

      return occurrences.map((occurrenceStart, occurrenceIndex) => {
        const displayStart = fromRRuleWallDate(occurrenceStart);
        const occurrenceEnd = addDuration(occurrenceStart, durationMs);
        return buildCalendarEvent({
          event,
          start: displayStart,
          end: addDuration(displayStart, durationMs),
          occurrenceStart,
          occurrenceEnd,
          occurrenceIndex,
          isRecurring,
          recurrenceText,
        });
      });
    } catch (error) {
      console.warn("failed to expand recurring event", event?._id, error);
      return [buildCalendarEvent({ event, start: baseStart, end: baseEnd })];
    }
  });
};

export const formatCalendarDateTime = (start, end) => {
  const startDate = toDate(start);
  const endDate = toDate(end);

  if (!startDate) return "";

  const date = startDate.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const startTime = startDate.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  const endTime = endDate?.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });

  return endTime ? `${date}, ${startTime} - ${endTime}` : `${date}, ${startTime}`;
};
